import React from 'react'
import PropTypes from 'prop-types'
import { Input } from 'antd'
import { compose, isEmpty, both, gt, __, length } from 'ramda'
import { classNames, isNumeric } from 'utils/utils'
import { isNumber } from 'utils/types'
import strings from 'utils/strings'
import InputLabel from 'components/patterns/Inputs/InputLabel'
import withStyle from '../Input.style.js'

class TextArea extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      charsRemaining: this.calculateCharsRemaining(this.props.value),
      requiredValueError: false,
      regExViolation: false,
      maxLengthError: false,
      isValid: true,
      hasBlurred: false,
    }
  }

  handlePressEnter = (e) => {
    const { onPressEnter, value } = this.props
    if (!onPressEnter) return

    // disable adding a new line
    if (!value || isEmpty(value) || !e.shiftKey) e.preventDefault()

    if (!e.shiftKey) {
      onPressEnter()
    }
  }

  valueViolatesDisallowedChars = (val = '') => {
    const { disallowedCharacters } = this.props
    if (!disallowedCharacters) return false
    return val.match(new RegExp(`[${disallowedCharacters.join('')}]`))
  }

  valueExceedsMaxLength = (val = '') => {
    const { maxLength } = this.props
    if (!maxLength) return false
    return val.length > maxLength
  }

  calculateCharsRemaining = (val = '') => {
    const { maxLength } = this.props
    if (!maxLength) return false
    return maxLength - val.length
  }

  valueRequiredError = (val) => {
    const { required } = this.props
    if (!required) return false
    return !val
  }

  validateValue = (val) => {
    const { customErrorMessage } = this.props
    const regExViolation = this.valueViolatesDisallowedChars(val)
    const requiredValueError = this.valueRequiredError(val)
    const maxLengthError = this.valueExceedsMaxLength(val)
    const isValid =
      !regExViolation &&
      !requiredValueError &&
      !maxLengthError &&
      !customErrorMessage

    return {
      regExViolation,
      requiredValueError,
      maxLengthError,
      isValid,
    }
  }

  /* eslint-disable no-bitwise, no-unused-expressions, react/no-did-update-set-state  */
  componentDidUpdate(prevProps, prevState) {
    const { hasBlurred } = this.state
    const { value, onValidityChange, name, paramName, required } = this.props
    if (prevProps.value !== value) {
      const validation = this.validateValue(value)
      const { isValid } = validation

      if (isValid ^ this.state.isValid) {
        onValidityChange &&
          onValidityChange(isValid, paramName || name, this.getErrorMsg())
      }

      this.setState({
        ...validation,
        charsRemaining: this.calculateCharsRemaining(value),
        ...(length(value) === 0 &&
          !required &&
          isValid &&
          hasBlurred && { hasBlurred: false }),
      })
    }
    /* eslint-enable no-bitwise, no-unused-expressions, react/no-did-update-set-state  */
  }

  componentDidMount() {
    const { value, onValidityChange, paramName, name } = this.props

    const validation = this.validateValue(value)
    const { isValid } = validation
    if (!isValid && onValidityChange) {
      onValidityChange(
        isValid,
        paramName || name,
        this.getFooterText(validation)
      )
    }
    this.setState(validation)
  }

  getFooterText = () => {
    const { customErrorMessage, maxLength, disallowedCharacters } = this.props
    const { regExViolation, requiredValueError, charsRemaining } = this.state
    if (requiredValueError) return strings.required
    if (customErrorMessage) return customErrorMessage
    if (regExViolation) return strings.disallowed_chars(disallowedCharacters)
    return maxLength && charsRemaining
  }

  getErrorMsg = () => {
    const text = this.getFooterText()
    return isNumeric(text) ? strings.value_exceeds_character_limit : text
  }

  handleChange = (event) => {
    const { onChange, maxLength, value } = this.props

    const eventValue = event.target.value

    const restrictInputLength = compose(
      both(gt(__, maxLength), gt(__, length(value))),
      length
    )

    if (maxLength && restrictInputLength(eventValue)) return {}

    return onChange(event)
  }

  handleBlur = (event) => {
    const { isValid } = this.state
    const { onBlur } = this.props
    this.setState({ hasBlurred: !isValid })
    onBlur(event)
  }

  render() {
    const {
      placeholder,
      value,
      disabled,
      name,
      className,
      inputClassName,
      label,
      customErrorMessage,
      box,
      vertical,
      prefix,
      suffix,
      noFooter,
      onFocus,
      autosize,
      hint,
      tooltip,
      required,
      size,
      'data-testid': dataTestId,
    } = this.props
    const { isValid, hasBlurred } = this.state

    const footerText = this.getFooterText()
    return (
      <div
        className={classNames(className, 'form-field', 'input-box', {
          box,
          'has-error': hasBlurred && (!isValid || customErrorMessage),
        })}
      >
        {label && (
          <InputLabel
            label={label}
            hint={hint}
            tooltip={tooltip}
            required={required}
            size={size}
            vertical={vertical}
          />
        )}
        <Input.TextArea
          autosize={autosize}
          value={value}
          placeholder={placeholder}
          disabled={disabled}
          name={name}
          className={classNames(inputClassName)}
          onChange={this.handleChange}
          onFocus={onFocus}
          onBlur={this.handleBlur}
          autoComplete="off"
          prefix={prefix}
          suffix={suffix}
          onPressEnter={this.handlePressEnter}
          data-testid={dataTestId}
        />
        {(hasBlurred || isNumber(footerText)) && !noFooter && footerText && (
          <header className="input-footer-textArea">{footerText}</header>
        )}
      </div>
    )
  }
}

TextArea.defaultProps = {
  maxLength: null,
  name: 'TextArea',
  size: 'default',
  width: 'auto',
  autosize: false,
  onChange: () => {},
  onBlur: () => {},
  onFocus: undefined,
  disabled: false,
  required: false,
}

TextArea.propTypes = {
  /** string value being edited */
  value: PropTypes.string,
  /** placeholder text displayed before the user interacts with the component */
  placeholder: PropTypes.string,
  /** boolean value determines whether or not the component is disabled */
  disabled: PropTypes.bool,
  /** boolean value describing whether the value is required or not */
  required: PropTypes.bool,
  /** Array of single character strings */
  disallowedCharacters: PropTypes.arrayOf(PropTypes.string),
  /** maximum number of characters allowed in the value */
  maxLength: PropTypes.number,
  /** callback fired when the the user focuses on the component */
  onFocus: PropTypes.func,
  /** callback fired when the component loses focus */
  onBlur: PropTypes.func,
  /** callback fired when the user updates the value */
  onChange: PropTypes.func,
  /** callback fired when value validity changes */
  onValidityChange: PropTypes.func,
  /** name value given to component and synthetic events fired within the component */
  name: PropTypes.string,
  /** name value displayed to user along with errors/user feedback */
  paramName: PropTypes.string,
  /** string value provided when an external error related to component value is generated */
  customErrorMessage: PropTypes.string,
  /** string css classname for further augmenting styles of the component wrapper */
  className: PropTypes.string,
  /** string css classname for further augmenting styles of the input element */
  inputClassName: PropTypes.string,
  /** string label displayed to the user above the component */
  label: PropTypes.string,
  /** boolean value determining box display behavior */
  box: PropTypes.bool,
  /** boolean value determining vertical display behavior */
  vertical: PropTypes.bool,
  /** text or icon embedded in the left side of the element */
  prefix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** text or icon embedded in the right side of the element */
  suffix: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  /** string value that determines element and related text size */
  size: PropTypes.oneOf(['small', 'default', 'large']),
  /** width value in pixels of the input element */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /** boolean to permanently hide footer if, for example, errors are displayed elsewhere in context */
  noFooter: PropTypes.bool,
  /** size that determines the presentation of the textarea. can be `true|false` or { minRows: 2, maxRows: 6} */
  autosize: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  /** optional string next to label */
  hint: PropTypes.string,
  /** optional string to render tooltip next to label */
  tooltip: PropTypes.string,
  onPressEnter: PropTypes.func,
  'data-testid': PropTypes.string,
}

export default compose(withStyle)(TextArea)
